import { FileSpreadsheet, FileText, TrendingUp } from 'lucide-react'
import { useRef, useState } from 'react'
import { CSVLink } from 'react-csv'
import type { Data } from 'react-csv/lib/core'

import { employersConsistentDeductions } from 'rails_routes'
import { Button, Modal } from 'slab'
import { useGetEmployersEmployees, usePostEmployersReportingEmployeeStatus } from 'utils/web_api'

import ReportRow from './ReportRow'
import { getLocaleDateString } from '../../../../utils'

const csvHeaders = [
  { label: 'Employee ID', key: 'id' },
  { label: 'First Name', key: 'first_name' },
  { label: 'Last Name', key: 'last_name' },
  { label: 'Email', key: 'email' },
]

export default function ReportsCard () {
  const currentYear = new Date().getFullYear()
  const [showEnrollmentsModal, setShowEnrollmentsModal] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)
  const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null)

  const { data: employeesData } = useGetEmployersEmployees()
  const createReportMutation = usePostEmployersReportingEmployeeStatus()

  const fetchDataForCSV = (): Data => {
    if (!employeesData?.data) return []
    return employeesData.data.map(employee => ({
      id: employee.id,
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.personal_email ?? employee.work_email ?? '',
    }))
  }

  const handleRosterDownload = () => {
    csvLinkRef.current?.link?.click()
  }

  const handleGenerateEnrollmentsReport = async () => {
    setIsGeneratingReport(true)

    try {
      await createReportMutation.mutateAsync({
        data: {
          plan_year: currentYear,
        }
      })

      setShowEnrollmentsModal(false)
    } catch (error) {
      console.error('Failed to create report:', error)
    } finally {
      setIsGeneratingReport(false)
    }
  }

  const handleDeductionsDownload = () => {
    window.location.href = employersConsistentDeductions.index()
  }

  return (
    <div className='flex flex-col gap-4'>
      <p className='body text-gray-500'>
        Reports
      </p>

      <div className='flex flex-col gap-2'>
        <ReportRow
          icon={FileSpreadsheet}
          label='Employee roster'
          onDownload={handleRosterDownload}
          disabled={!employeesData?.data?.length}
        />

        <ReportRow
          icon={TrendingUp}
          label='Year-to-date enrollments'
          onDownload={() => setShowEnrollmentsModal(true)}
        />

        <ReportRow
          icon={FileText}
          label='Employee deductions report'
          onDownload={handleDeductionsDownload}
        />
      </div>

      <CSVLink
        ref={csvLinkRef}
        data={fetchDataForCSV()}
        headers={csvHeaders}
        filename={`employee-roster-${currentYear}-${getLocaleDateString(new Date())}.csv`}
        target='_blank'
        style={{ display: 'none' }}
      />

      <Modal
        open={showEnrollmentsModal}
        onOpenChange={setShowEnrollmentsModal}
        width='medium'
        aria-describedby={undefined}
      >
        <Modal.Header>
          <Modal.Title>Request year-to-date enrollments report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='space-y-4'>
            <div>
              <p className='mb-3'>
                This report includes:
              </p>
              <ul className='list-disc pl-5 space-y-1 text-sm'>
                <li>Allowances and plan premiums</li>
                <li>Employee plan enrollment details</li>
                <li>Coverage dates and dependent information</li>
              </ul>
            </div>

            <p className='text-sm'>
              This report may take a few minutes to generate.
              You'll receive an email with a download link once it's ready.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex justify-end gap-3 w-full'>
            <Modal.Button
              variant='secondary'
              disabled={isGeneratingReport}
            >
              Cancel
            </Modal.Button>
            <Button
              variant='primary'
              onClick={handleGenerateEnrollmentsReport}
              disabled={isGeneratingReport}
              leftIcon={isGeneratingReport ? undefined : FileSpreadsheet}
            >
              {isGeneratingReport ? 'Generating...' : 'Generate report'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
